import { useEffect } from "react"

import { ArrowRight, BadgePoundSterling, Box, MapPin, Truck } from "lucide-react"
import { Link, useSearchParams } from "react-router-dom"

import SignOutRedirect from "@/components/auth/SignOutRedirect"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { useAnonymousVisitor } from "@/hooks/useAnonymousVisitor"
import { useFetchActiveJobs } from "@/hooks/useJobQueries"

const LandingPage = () => {
  const { createVisitor } = useAnonymousVisitor()
  const { data: jobs, isLoading, error } = useFetchActiveJobs()
  const [searchParams] = useSearchParams()

  const getIconForJobType = (type: string) => {
    switch (type) {
      case "Driver":
        return <Truck className="mr-1 h-4 w-4" />
      case "Warehouse":
        return <Box className="mr-1 h-4 w-4" />
      default:
        return <Truck className="mr-1 h-4 w-4" />
    }
  }

  useEffect(() => {
    // Get source parameter from URL if it exists
    const source = searchParams.get("source")

    // Create visitor with source information if available
    createVisitor(source ? { source } : undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-6xl px-4 py-8">
        {/* Logo Section */}
        <div className="mb-8 flex justify-center">
          <img src="/images/Hived-logo.svg" alt="Hived Logo" className="h-20 w-20" />
        </div>

        {/* Hero Section */}
        <div className="mb-8 text-center">
          <h1 className="mb-4 text-4xl font-bold text-jungle md:text-5xl">
            Current Open Positions
          </h1>
          <p className="mb-8 text-xl text-gray-600">
            Join our growing team with delivery and warehouse opportunities across the UK
          </p>
        </div>

        {/* Job Postings */}
        <div className="mx-auto flex max-w-2xl flex-col space-y-6">
          {isLoading && (
            <div className="py-10 text-center">
              <p className="text-gray-600">Loading job postings...</p>
            </div>
          )}

          {error && (
            <div className="py-10 text-center">
              <p className="text-red-500">{error.message}</p>
              <Button
                className="mt-4 bg-honeycomb text-white hover:bg-amber-500"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </div>
          )}
          {!isLoading && !error && jobs.length === 0 && (
            <div className="py-10 text-center">
              <p className="text-gray-600">No job postings available at this time.</p>
            </div>
          )}

          {!isLoading &&
            !error &&
            jobs.map((job) => (
              <Card key={job.id} className="p-6 transition-shadow hover:shadow-lg">
                <div className="mb-4">
                  <h3 className="mb-2 text-xl font-bold text-jungle">{job.title}</h3>
                  <div className="mb-2 flex items-center text-gray-600">
                    <MapPin className="mr-1 h-4 w-4" />
                    <span className="text-sm">{job.location}</span>
                  </div>
                  <div className="mb-2 flex items-center text-gray-600">
                    {getIconForJobType(job.type)}
                    <span className="text-sm">{job.type}</span>
                  </div>
                  {job.salary && (
                    <div className="flex items-center font-semibold text-honeycomb">
                      <BadgePoundSterling className="mr-1 h-4 w-4" />
                      <span className="text-sm">{job.salary}</span>
                    </div>
                  )}
                </div>

                <p className="mb-4 text-sm text-gray-600">{job.description}</p>

                <SignOutRedirect job={job} />
              </Card>
            ))}
        </div>

        {/* Login Section - Matching basic details page style */}
        <div className="my-[2px] mt-8 py-[24px] text-center">
          <p className="mb-2 text-sm text-gray-500">Already started an application?</p>
          <Link to="/login">
            <Button variant="outline" className="my-0 w-full py-0 md:w-auto">
              Return to your application <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        {/* HQ Jobs Section */}
        <div className="my-[2px] flex items-center justify-center border-t border-gray-200 py-[24px] text-center">
          <p className="mr-2 text-sm text-gray-500">Looking for HQ jobs?</p>
          <a
            href="https://jobs.ashbyhq.com/hived"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center text-sm"
          >
            View HQ opportunities.
          </a>
        </div>
      </div>
    </div>
  )
}

export default LandingPage
